/**
 * Compact input group for the 5 supported social handles.
 *
 * Stores the result in `value` as a plain object like:
 *   { instagram: "@theblkcommunity", facebook: "...", twitter: "...",
 *     tiktok: "...", linkedin: "...", youtube: "..." }
 *
 * Empty fields are stripped before save by the caller. Accepts either a
 * full URL or just a handle ("@something" or "username") — the BusinessProfile
 * page resolves the proper link.
 *
 * Usage:
 *   <SocialLinksInput
 *     value={businessForm.social_links || {}}
 *     onChange={(next) => setBusinessForm({...businessForm, social_links: next})}
 *   />
 */
import { Instagram, Facebook, Twitter, Linkedin, Music2, Youtube, Ghost, Twitch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "@yourhandle" },
  { key: "tiktok",    label: "TikTok",    icon: Music2,    placeholder: "@yourhandle" },
  { key: "facebook",  label: "Facebook",  icon: Facebook,  placeholder: "yourpage" },
  { key: "youtube",   label: "YouTube",   icon: Youtube,   placeholder: "@yourchannel" },
  { key: "snapchat",  label: "Snapchat",  icon: Ghost,     placeholder: "yourusername" },
  { key: "twitch",    label: "Twitch",    icon: Twitch,    placeholder: "yourchannel" },
  { key: "twitter",   label: "X / Twitter", icon: Twitter, placeholder: "@yourhandle" },
  { key: "linkedin",  label: "LinkedIn",  icon: Linkedin,  placeholder: "yourcompany" },
];

export default function SocialLinksInput({ value = {}, onChange }) {
  const setOne = (key, v) => {
    const next = { ...(value || {}) };
    if (v.trim()) next[key] = v.trim();
    else delete next[key];
    onChange(next);
  };

  return (
    <div data-testid="social-links-input">
      <Label className="mb-2 block">Social handles <span className="text-gray-400 text-xs">(optional)</span></Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PLATFORMS.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-tbc-navy/5 flex items-center justify-center text-tbc-navy flex-shrink-0">
              <Icon className="w-4 h-4" />
            </div>
            <Input
              value={value?.[key] || ""}
              onChange={(e) => setOne(key, e.target.value)}
              placeholder={`${label}: ${placeholder}`}
              data-testid={`social-input-${key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Public-side helpers ------------------------------------------------------

const BASE_URLS = {
  instagram: "https://instagram.com/",
  facebook:  "https://facebook.com/",
  twitter:   "https://twitter.com/",
  tiktok:    "https://tiktok.com/@",
  linkedin:  "https://linkedin.com/company/",
  youtube:   "https://youtube.com/",
  snapchat:  "https://snapchat.com/add/",
  twitch:    "https://twitch.tv/",
};

/** Convert a stored handle (URL or @handle or username) → a clickable URL. */
export function resolveSocialUrl(platform, raw) {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "");
  const base = BASE_URLS[platform];
  if (!base) return null;
  // TikTok needs the @ injected back since base already includes it
  return base + handle;
}

export const SOCIAL_PLATFORMS = PLATFORMS;
